<template>
  <div>
    <success-component
        v-if="success.length > 0"
        :message="success">
    </success-component>
    <error-component
        v-if="exceptions.length > 0"
        v-bind="exceptions[0]">
    </error-component>
    <div class="toolbar">
      <b-form-input class="title border-0 pl-2"
                    placeholder="Untitled Visualization"
                    size="lg"
                    type="text"
                    name="name"
                    v-model="title"/>
      <em class="text-danger" v-if="errors.has('name')">{{ errors.first('name') }}</em>
      <b-navbar class="visualization-nav p-0 pl-1" toggleable="lg">
        <b-navbar-toggle target="nav_dropdown_collapse"></b-navbar-toggle>
        <b-collapse id="nav_dropdown_collapse" is-nav>
          <b-navbar-nav>
            <b-nav-item-dropdown left text="Visualization">
              <b-dropdown-item @click="save">Save</b-dropdown-item>
              <b-dropdown-item v-if="visualization_id" @click="newVisualization()">New</b-dropdown-item>
            </b-nav-item-dropdown>
            <b-nav-item-dropdown left text="Reports">
              <div v-if="reports.length > 0">
                <b-dropdown-item
                    v-for="report in reports"
                    :key="report.id"
                    @click="goEdit(report.id)">
                  {{report.title}}
                </b-dropdown-item>
              </div>
              <b-dropdown-item @click="goCreate('report')">
                new
              </b-dropdown-item>
            </b-nav-item-dropdown>
            <b-nav-item @click="showShareModal('Share')">
              <font-awesome-icon icon="share-square"/>
              Share
            </b-nav-item>
            <b-nav-item @click="showShareModal('Embed')">
              <font-awesome-icon icon="code"/>
              Embed
            </b-nav-item>
            <b-nav-item-dropdown left>
              <template slot="button-content">
                <font-awesome-icon icon="download"/>
                Export
              </template>
              <b-dropdown-item @click="exportAs('png')" v-if="isChart">
                As PNG
              </b-dropdown-item>
              <b-dropdown-item @click="exportAs('csv')">
                As CSV
              </b-dropdown-item>
              <b-dropdown-item @click="exportAs('xls')">
                As XLS
              </b-dropdown-item>
            </b-nav-item-dropdown>
            <b-nav-item @click="reload" v-b-tooltip.hover title="Get fresh data">
              <font-awesome-icon icon="sync-alt"/>
              Reload
            </b-nav-item>
          </b-navbar-nav>
        </b-collapse>
        <div class="row pull-right mr-2">
          <div v-if="saving"
               class="mt-3">
            <tiny-bouncer/>
          </div>
          <div>
            <toggle-button :labels="{checked: 'Public', unchecked: 'Private'}" :sync="true"
                           :value="status === 'public'"
                           :width="70"
                           @change="onToggleChange"
                           name="toggleButton"
                           style="margin-top: 7px">
            </toggle-button>
          </div>
        </div>
      </b-navbar>
      <hr class="mb-0">

      <share-modal-component
          ref="myModal"
          :status="status"
          :origin="'visualization'"
          :id="visualization_id"
          @show="show()"
          :action="action"
          :base-url="baseUrl">
      </share-modal-component>
    </div>
    <div class="row m-0">
      <div class="col-md-10 col-sm-10">
        <div v-if="parameters.length > 0" class="mt-3">
          <parameters-component></parameters-component>
        </div>
        <div v-if="chartType === 'table'" class="results my-3" style="max-height: 1000px; width: 100%; overflow: auto">
          <ag-grid-wrapper
                        :items="data"
                        :fields="columnsTableSelected"
                        :paginate="true"
                        :showSidebar="true"
          :fieldsDefinition="fieldsDefinition"
                        :location="'create'"
                        ref="agGrid"/>
        </div>
        <div v-if="chartType !== 'table' && chartType !== 'bigNumber'" :key="updating">
          <chart-js ref="chartComponent" v-bind:chartData="chartData"
                    v-if="chart_library === 'chartjs'"></chart-js>
        </div>
        <div v-if="chartType === 'bigNumber'">
          <big-number ref="bigNumberComponent" v-bind:chartData="chartData"></big-number>
        </div>
      </div>
      <div class="col-md-2 col-sm-2 p-0 sideTool">
        <aside class="toolbox option-sidebar">
          <section class="sidebar">
            <label>Description:</label>
            <b-form-textarea v-model="description"
                             placeholder="Enter a description"
                             class="mb-2"
                             :rows="2"
                             :max-rows="2">
            </b-form-textarea>
            <label>Type:</label>
            <div class="mb-2">
              <b-button-group>
                <b-button variant="primary" :class="{ active: chartType === 'table'}"
                          @click="chartType = 'table'" title="Table"
                          v-b-tooltip.hover>
                  <font-awesome-icon icon="table"/>
                </b-button>
                <b-button variant="primary" :class="{ active: chartType !== 'table' && chartType !== 'bigNumber'}"
                          @click="chartType = 'vbar'" title="Chart"
                          v-b-tooltip.hover>
                  <font-awesome-icon icon="chart-line"/>
                </b-button>
                <b-button variant="primary" :class="{ active: chartType === 'bigNumber'}"
                          @click="chartType = 'bigNumber'" title="Number"
                          v-b-tooltip.hover>
                  <font-awesome-icon icon="sort-numeric-down"/>
                </b-button>
              </b-button-group>
            </div>

            <div class="clearfix">
              <div class="pull-left">
                <label>Source Query:</label>
              </div>
              <div class="pull-right">
                <a @click.prevent="editQuery(query_id)" class="editquery" href="#" title="Edit this Query" v-b-tooltip.hover>
                  <font-awesome-icon icon="edit"/>
                </a>
              </div>
            </div>
            <b-form-select class="custom-select mb-2" name="query" v-model="query_id" :state="errors.has('query') ? false: null"
                           v-validate:query_id="'selectValidator'">
              <option disabled selected :value="-1">
                Select Query
              </option>
              <option :key="query.id"
                      :value="query.id"
                      v-for="query in queries">
                {{ query.name }}
              </option>
            </b-form-select>
            <em v-if="errors.has('query')" class="text-danger">{{ errors.first('query') }}</em>
            <div v-if="loading.query">
              <spinner-component></spinner-component>
            </div>
            <div v-else>
              <b-form-group v-if="chartType === 'bigNumber'">
                <label>Select Field:</label>
                <b-form-select class="custom-select mb-2" name="Field" v-validate="'selectValidator'"
                               v-model="field" :state="errors.has('Field') ? false: null">
                  <option disabled selected v-bind:value="-1">
                    Select field
                  </option>
                  <option v-bind:key="col"
                          v-bind:value="col"
                          v-for="col in columns">
                    {{ col }}
                  </option>
                </b-form-select>
                <em v-if="errors.has('Field')" class="text-danger">{{ errors.first('Field') }}</em>
              </b-form-group>



              <div v-if="chartType !== 'table' && chartType !== 'bigNumber'">
                <label>Chart:</label>
                <div class="mb-2">
                  <b-button class="mb-2 mr-1" variant="primary" :class="{ active: chartType === 'vbar' }"
                            @click="chartType = 'vbar'" title="Vertical Bar"
                            v-b-tooltip.hover>
                    <font-awesome-icon icon="chart-bar"/>
                  </b-button>
                  <b-button class="mb-2 mr-1" variant="primary" :class="{ active: chartType === 'bar' }"
                            @click="chartType = 'bar'" title="Horizontal Bar"
                            v-b-tooltip.hover>
                    <font-awesome-icon icon="chart-bar" class="chart-bar"/>
                  </b-button>
                  <b-button class="mb-2 mr-1" variant="primary" :class="{ active: chartType === 'pie' }"
                            @click="chartType = 'pie'" title="Pie"
                            v-b-tooltip.hover>
                    <font-awesome-icon icon="chart-pie"/>
                  </b-button>
                  <b-button class="mb-2 mr-1" variant="primary" :class="{ active: chartType === 'line'}"
                            @click="chartType = 'line'" title="Line"
                            v-b-tooltip.hover>
                    <font-awesome-icon icon="chart-line"/>
                  </b-button>
                  <b-button class="mb-2 mr-1" variant="primary" :class="{ active: chartType === 'area' }"
                            @click="chartType = 'area'" title="Area"
                            v-b-tooltip.hover>
                    <font-awesome-icon icon="chart-area"/>
                  </b-button>
                  <b-button class="mb-2 mr-1" variant="primary" :class="{ active: chartType === 'scatter' }"
                            @click="chartType = 'scatter'" title="Scatter"
                            v-b-tooltip.hover>
                    <font-awesome-icon icon="ellipsis-h" class="ellipsis-h"/>
                  </b-button>
                  <b-button class="mb-2 mr-1" variant="primary" :class="{ active: chartType === 'mixed' }"
                            @click="chartType = 'mixed'" title="Mixed"
                            v-b-tooltip.hover>
                    <font-awesome-icon icon="layer-group"/>
                  </b-button>
                </div>

                <label v-if="['vbar', 'bar', 'line', 'area', 'scatter', 'mixed'].includes(chartType)">
                  X axis:
                </label>
                <label v-if="chartType === 'pie'">
                  Labels:
                </label>
                <div v-if="chartType !== 'table'">
                  <b-form-select class="custom-select mb-2" name="X axis" v-validate="'selectValidator'"
                                 v-model="dim" :state="errors.has('X axis') ? false: null">
                    <option disabled selected v-bind:value="-1">
                      Select field
                    </option>
                    <option v-bind:key="col"
                            v-bind:value="col"
                            v-for="col in columns">
                      {{ col }}
                    </option>
                  </b-form-select>
                  <em class="text-danger" v-if="errors.has('X axis')">{{ errors.first('X axis') }}</em>
                </div>

                <label class="d-block" v-if="['vbar', 'bar', 'line', 'area', 'scatter', 'mixed'].includes(chartType)">
                  Y axis:
                </label>
                <b-form-checkbox class="mb-2" v-model="stackedBar" v-if="['vbar', 'bar'].includes(chartType)">
                  Stacked bar
                </b-form-checkbox>
                <label v-if="chartType === 'pie'">
                  Values:
                </label>
                <div v-if="chartType !== 'table'">
                  <div v-if="chartType === 'mixed'">
                    <div class="clearfix mb-2" v-for="(metric, index) in metricMixed" :key="index">
                      <div class="pull-left mr-2">
                        <b-dropdown variant="link" no-caret>
                          <template slot="button-content" id="pepe">
                            <font-awesome-icon :icon="metricMixed[index].icon" :class="{'chart-bar' : metricMixed[index].type === 'bar'}"/>
                          </template>
                          <b-dropdown-item href="#" @click="changeTypeDataset(index, 'vbar', 'chart-bar')"><font-awesome-icon icon="chart-bar"/> Vertical Bar</b-dropdown-item>
                          <b-dropdown-item href="#" @click="changeTypeDataset(index, 'bar', 'chart-bar')"><font-awesome-icon icon="chart-bar" class="chart-bar"/> Horizontal Bar</b-dropdown-item>
                          <b-dropdown-item href="#" @click="changeTypeDataset(index, 'line', 'chart-line')"><font-awesome-icon icon="chart-line"/> Line</b-dropdown-item>
                          <b-dropdown-item href="#" @click="changeTypeDataset(index, 'area', 'chart-area')"><font-awesome-icon icon="chart-area"/> Area</b-dropdown-item>
                          <b-dropdown-item href="#" @click="changeTypeDataset(index, 'scatter', 'ellipsis-h')"><font-awesome-icon icon="ellipsis-h" class="ellipsis-h"/> Scatter</b-dropdown-item>
                        </b-dropdown>
                      </div>
                      <div class="pull-left mr-1">
                        <b-form-select class="custom-select" name="axis"
                                       v-model="metric.field" :state="errors.has('axis') ? false: null">
                          <option disabled selected v-bind:value="-1">
                            Select field
                          </option>
                          <option v-bind:key="col"
                                  v-bind:value="col"
                                  v-for="col in columns">
                            {{ col }}
                          </option>
                        </b-form-select>
                        <em v-if="errors.has('axis')" class="text-danger">{{ errors.first('axis') }}</em>
                      </div>
                      <a href="#" v-if="metricMixed.length > 1" @click.prevent="delMetric(index)" class="text-danger pull-left mt-2" v-b-tooltip.hover title="Delete">
                        <font-awesome-icon icon="times-circle" />
                      </a>
                    </div>
                    <div class="mt-1">
                      <a href="#" class="addmetric" @click.prevent="addDataset">
                        <font-awesome-icon icon="plus-circle"/>
                        Add another
                      </a>
                    </div>
                  </div>
                  <div v-else>
                    <b-form-select class="custom-select mb-2" name="Y axis" v-validate="'selectValidator'"
                                   v-model="metric" :state="errors.has('Y axis') ? false: null">
                      <option disabled selected v-bind:value="-1">
                        Select field
                      </option>
                      <option :key="col"
                              :value="col"
                              v-for="col in columns">
                        {{ col }}
                      </option>
                    </b-form-select>
                    <em class="text-danger" v-if="errors.has('Y axis')">{{ errors.first('Y axis') }}</em>
                    <div v-if="['vbar', 'bar'].includes(chartType) && stackedBar">
                      <div class="clearfix mb-2" v-for="(metric,index) in metricStacked" :key="index">
                        <div class="pull-left mr-1">
                          <b-form-select class="custom-select" v-model="metric.field">
                            <option disabled selected v-bind:value="-1">
                              Select field
                            </option>
                            <option v-bind:key="col"
                                    v-bind:value="col"
                                    v-for="col in columns">
                              {{ col }}
                            </option>
                          </b-form-select>
                        </div>
                        <a href="#" v-if="metricStacked.length > 0" @click.prevent="delMetricStacked(index)" class="text-danger pull-left mt-2" v-b-tooltip.hover title="Delete">
                          <font-awesome-icon icon="times-circle" />
                        </a>
                      </div>
                      <a href="#" class="addmetric" @click.prevent="addDatasetStacked">
                        <font-awesome-icon icon="plus-circle"/>
                        Add another
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="mt-2">
              <b-button @click.prevent="getBack" variant="secondary pull-left mr-2">
                <font-awesome-icon icon="arrow-left"/>
                Back
              </b-button>
              <b-button @click="graph" variant="success pull-left">
                Graph
                <b-spinner
                    v-if="loading.graph"
                    small
                    class="ml-2" />
              </b-button>
            </div>
          </section>
        </aside>
      </div>
    </div>
  </div>
</template>

<script>
  import SpinnerComponent from '../../components/Spinner-loader.vue';
  import chartJs from '../../graphs/chartJs';
  import ToggleButton from 'vue-js-toggle-button/src/Button';
  import ErrorComponent from '../../components/ErrorView.vue';
  import SuccessComponent from '../../components/Success.vue';
  import bigNumber from "../../graphs/bigNumber";
  import TinyBouncer from '../../components/TinyBouncer.vue';
  import ParametersComponent from './Parameters.vue';
  import ShareModalComponent from '../../components/ShareModal.vue';
  import exportChart from '../../mixins/exportChart';

  import { createHelpers } from 'vuex-map-fields';
  import {mapGetters, mapState} from 'vuex';
  import AgGridWrapper from '../../components/AgGridWrapper.vue';
  import _ from 'lodash';

  const { mapFields } = createHelpers({
    getterType: 'visualizations/getField',
    mutationType: 'visualizations/updateField',
  });

  let chartDataInitialState;

  import { Validator } from 'vee-validate';
  import XlsExport from 'xlsexport/xls-export'

  Validator.extend('selectValidator', {
    getMessage: field => 'The ' + field + ' field is required.',
    validate: value => value !== -1 && value !== ''
  });

  export default {
    name: 'visualization',
    mixins: [exportChart],
    props: {
      visualization_id: {
        required: false
      },
      queryId: {
        required: false
      },
      queryTitle: {
        type: String,
        required: false
      }
    },
    components: {
      chartJs,
      bigNumber,
      ToggleButton,
      ErrorComponent,
      SuccessComponent,
      ParametersComponent,
      SpinnerComponent,
      ShareModalComponent,
      TinyBouncer,
      AgGridWrapper
    },
    $_veeValidate: {
      validator: 'new'
    },
    data() {
      return {
        chart: '',
        showGraph: '',
        firstGraph: true,
        columns: '',
        columnsTable: [],
        columnsTableSelected: [],
        filteredArray: [],
        pie: {
          value: -1,
          label: -1
        },
        loading: {
          query: false,
          graph: false,
        },
        action: '',
        updating: 0,
        saving: false
      }
    },
    computed: {
      ...mapGetters({
        organizationSelected: 'getOrganizationSelected',
      }),
      ...mapState({
        queries: state => state.queries.items,
        baseUrl: state => state.baseUrl,
        fieldsTable: state => state.visualizations.chartData.fieldsTable,
        fieldsDefinition: state => state.visualizations.chartData.fieldsDefinition,
        pivotMode: state => state.visualizations.chartData.pivotMode}),
      ...mapState({
        exceptions: 'exceptions',
        success: 'success'
      }),
      ...mapFields([
        'chartData',
        'chartData.chartType',
        'chartData.chart_library',
        'chartData.selector',
        'chartData.title',
        'chartData.width',
        'chartData.height',
        'chartData.metric',
        'chartData.metricMixed',
        'chartData.metricStacked',
        'chartData.dim',
        'chartData.data',
        'chartData.field',
        'chartData.description',
        'chartData.options',
        'chartData.query_id',
        'chartData.status',
        'chartData.stackedBar',
        'parameters',
        'reports'
      ]),
      isBigNumber() {
        return this.chartType === 'bigNumber';
      },
      isChart() {
        return !this.isBigNumber && !this.isTable;
      },
      isTable() {
        return this.chartType === 'table';
      }
    },
    created() {
      this.$store.commit('visualizations/SET_CHARTDATA_INITIAL_STATE');
      chartDataInitialState = _.cloneDeep(this.chartData);

      this.$store.dispatch('queries/fetchAll', this.$store.getters.getOrganizationSelected).then(() => {
        if (this.queryId) {
          this.query_id = this.queryId;
        }
      });

      if (this.visualization_id) {
        this.$store.dispatch('visualizations/fetchVisualization', this.visualization_id);
        this.$store.dispatch('visualizations/getReports', this.visualization_id);
      }
      if (this.queryTitle) {
        this.title = this.queryTitle;
      }
    },
    beforeDestroy() {
      this.reset();
    },
    methods: {
      addDataset() {
        let obj = {
          type: 'bar',
          field: -1,
          icon: 'chart-bar',
        };
        this.metricMixed.push(obj);
      },
      addDatasetStacked() {
        let obj = {
          type: 'bar',
          field: -1,
        };
        this.metricStacked.push(obj);
      },
      changeTypeDataset(index, type, fa) {
        this.metricMixed[index].type = type;
        this.metricMixed[index].icon = fa;
      },
      delMetric(index) {
        if (this.metricMixed.length > 1)
          this.metricMixed.splice(index, 1);
      },
      delMetricStacked(index) {
        if (this.metricStacked.length > 0)
          this.metricStacked.splice(index, 1);
      },
      exportAs(type) {
        this.$validator.validateAll().then((result) => {
          if (result) {
            this.exportChart(type)
          }
        });
      },
      generateLinks(path) {
        if (this.$props.visualization_id) {
          let visualizationUrl = `${this.baseUrl}/#/${path}/${this.visualization_id}`;
          this.$clipboard(visualizationUrl);
          this.$swal('Link copied to clipboard');
        } else {
          this.$swal('Save your report first');
        }
      },
      graph() {
        return new Promise(resolve => {
          this.$validator.validateAll().then((result) => {
            if (result) {
              this.loading.graph = true;
              if (this.chartType === 'table') {
                if (this.fieldsTable.length > 0) {
                  if (this.firstGraph) {
                    this.columnsTableSelected = this.fieldsTable;
                    this.firstGraph = false;
                    if (this.visualization_id) {
                      this.$nextTick(() => {
                        this.$refs.agGrid.gridOptions.columnApi.setColumnState(this.fieldsDefinition);
                        this.$refs.agGrid.gridOptions.columnApi.setPivotMode(this.pivotMode);
                      })
                    }
                  }
                }
              } else if (this.chartType === 'bigNumber') {
                this.$refs.bigNumberComponent.graph();
              } else if (this.chart_library === 'chartjs') {
                this.$refs.chartComponent.graph();
              }
              if (!this.firstGraph || this.visualization_id === undefined) {
                this.save();
              }

              this.loading.graph = false;
              resolve();
            }
          });
        });
      },
      getBack() {
        this.$router.go(-1);
      },
      onToggleChange(val) {
        let status = 'private';
        if (val.value) {
          status = 'public';
        }
        this.status = status;
        if (this.visualization_id) {
          this.save();
        }
      },
      reload() {
        let params = {
          parameters: [],
          refresh: true
        };
        if (this.parameters !== "") {
          this.parameters.forEach(function (param) {
            params.parameters.push(param);
          });
        }
        let payload = {
          queryId: this.query_id,
          refresh: true,
        };
        this.$store.dispatch('queries/executeQuery', payload).then(response => {
          this.data = response.data.data.results;
          this.graph();
        });
      },
      save() {
        this.$validator.validate('query').then((result) => {
          if (result) {
            this.updateColumnsSelected().then(() => {
              this.saving = true;
              if (this.visualization_id) {
                this.$store.dispatch('visualizations/save', this.visualization_id).then(() => {
                  this.saving = false;
                });
              }
              else {
              this.$store.dispatch('visualizations/save').then((res) => {
                this.saving = false;
                let id = res.data.data.visualization.id;
                this.$router.replace({
                  name: 'visualization.edit',
                  params: {
                    visualization_id: id}
                  });
                });
              }
            })
          }
        })
      },
      showShareModal(action) {
        if (this.visualization_id) {
          this.action = action;
          this.$refs.myModal.show();
        } else {
          this.$swal('Save your visualization first');
        }
      },
      editQuery(id) {
        this.$router.push({name: 'query.edit', params: {query_id: id}});
      },
      restarLoading() {
        return new Promise(resolve => {
          this.loading.query = false;
          resolve();
        })
      },
      goCreate(resource) {
        this.$router.push({ name: `${resource}.create`, params:{visualization_id: this.visualization_id} });
      },
      reset() {
        return new Promise(resolve => {
          this.$store.commit('visualizations/SET_CHARTDATA_INITIAL_STATE');
          this.$store.commit('queries/SET_QUERIES', []);
          this.$store.commit('queries/SET_PARAMETERS', []);
          this.$store.commit('queries/SET_RESULTS', []);
          this.$store.commit('visualizations/SET_VISUALIZATION_PARAMETERS', []);
          this.$store.commit('visualizations/SET_PARAMETERS');
          this.$store.commit('visualizations/SET_VISUALIZATION_REPORTS', []);
          this.$store.commit('visualizations/SET_FIELDS_TABLE', []);this.$validator.reset();
          this.$store.dispatch('clearMessages');
          this.columnsTableSelected = [];
                    this.firstGraph = true;resolve();
        })
      },
      goEdit(id) {
        this.$router.push({ name: 'report.edit', params: {report_id: id} });
      },
      newVisualization() {
        this.updating = 1;
        this.reset().then(() => {
          this.$store.dispatch('queries/fetchAll', this.$store.getters.getOrganizationSelected).then(() => {
            this.updating = 0;
          });
          this.$router.replace({ name: 'visualization.create' });
        });
      },
      updateColumnsSelected() {
        if (this.chartData.chartType !== 'table') {
          return new Promise(resolve => {
            resolve();
          });
        }
        return new Promise(resolve => {
            let columnsDefinition = this.$refs.agGrid.gridOptions.columnApi.getColumnState(),
                columns = this.$refs.agGrid.gridOptions.columnApi.getAllColumns(),
                fields = [],
                pivotMode = this.$refs.agGrid.gridOptions.columnApi.isPivotMode();

                columns.forEach(column => {
                    if (column.userProvidedColDef) {
                        fields.push(column.userProvidedColDef.headerName);
                    }
                });

            this.$store.commit('visualizations/SET_FIELDS_TABLE', fields);
            this.$store.commit('visualizations/SET_FIELDS_DEFINITION', columnsDefinition);
            this.$store.commit('visualizations/SET_PIVOT_MODE', pivotMode);
            resolve();
      })
      }
    },
    watch: {
      'query_id': function (newVal) {
        if (newVal !== -1) {
          this.restarLoading().then(() => {
            this.loading.query = true;
          });

          let payload = {
            queryId: newVal,
            refresh: false,
            loadParameters: true,
          };

          this.$store.dispatch('queries/executeQuery', payload).then(response => {
            this.data = response.data.data.results;
            this.columns = Object.keys(this.data[0]);
            this.columnsTable = this.columns.map(el => {
              return {text: el, value: el}
            });

            if (this.firstGraph && this.visualization_id) {
              if (this.fieldsTable.length === 0) {
                this.$store.commit('visualizations/SET_FIELDS_TABLE', Object.keys(this.data[0]));
              }
              this.graph().then(() => {
                this.firstGraph = false;
              });
            } else {
              this.$store.commit('visualizations/SET_FIELDS_TABLE', Object.keys(this.data[0]));

              this.metric = [{
                type: '',
                field: -1,
              }];
              this.dim = -1;
            }
            this.loading.query = false;
            this.$store.dispatch('setSuccess', 'Retrieved')
          }).catch((error) => {
            this.loading.query = false;
            this.$store.commit('SET_EXCEPTIONS', error.response.data);
          });
        }
      },
      'organizationSelected'(newValue) {
        this.reset();
        this.$store.dispatch('queries/fetchAll', newValue);
        this.$router.replace({name: 'visualization.create'});
      }
    },
  }
</script>

<style scoped>
  label {
    margin-bottom: 0.2rem;
  }
</style>
